import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";

import { api } from "~/api";
import { Button } from "~/components/Button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/card";
import { CopyButton } from "~/components/CopyButton";
import { Icon } from "~/components/icon";
import { SessionExpired } from "~/components/SessionExpired";
import { StoragePrefixes } from "~/components/StoragePrefixes";
import { useAuth } from "~/contexts/auth";
import { useKeybindings } from "~/hooks/useKeyBindings";
import { ClusterOperationsCommandPalette } from "~/routes/_index/components/ClusterOperationsCommandPalette";
import type { Cluster, Namespace } from "~/types";
import {
  formatBytes,
  formatNumber,
  getDatadogLogsUrl,
  getDatadogTracesUrl,
} from "~/utils";
import { namespacePrefixes } from "~/utils/namespacePrefix";
import { formatRelativeUtc } from "~/utils/time";

function StatCell({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg bg-gray-50 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold text-gray-900">{value}</div>
    </div>
  );
}

function fmt(d: string | null) {
  if (!d) return "—";
  return formatRelativeUtc(new Date(d), new Date());
}

function NamespaceDetailsCard({
  ns,
  onForceReindex,
  reindexDisabledReason,
  onTriggerFullCompaction,
  onPrintTableState,
  tidyDisabledReason,
  onWarmCache,
  warmCacheDisabledReason,
  clusterError,
}: {
  ns: Namespace;
  onForceReindex: () => void;
  /** Set disables the button and becomes its tooltip. */
  reindexDisabledReason?: string;
  onTriggerFullCompaction: () => void;
  onPrintTableState: () => void;
  /** Same, shared by both actions that run as tidy jobs. */
  tidyDisabledReason?: string;
  onWarmCache: () => void;
  warmCacheDisabledReason?: string;
  clusterError?: string;
}) {
  const isTruncated = ns.id.length > 55;
  const [storagePrefixes, setStoragePrefixes] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    setStoragePrefixes([]);
    void namespacePrefixes(
      ns.org_id,
      ns.id,
      ns.shards,
      ns.sharding_version,
    ).then((prefixes) => {
      if (!cancelled) setStoragePrefixes(prefixes);
    });
    return () => {
      cancelled = true;
    };
  }, [ns.org_id, ns.id, ns.shards, ns.sharding_version]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-mono">
          <span className="inline-flex items-center gap-2">
            {isTruncated ? `${ns.id.slice(0, 55)}...` : ns.id}
            <CopyButton value={ns.id} label="Copy namespace ID" />
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Identity — 2 cols + full-width storage prefix */}
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Org ID
              </div>
              <div className="mt-1 flex items-center gap-2 font-mono text-sm text-gray-900">
                <Link
                  to={`/orgs/${ns.org_id}`}
                  className="underline underline-offset-2 hover:text-foreground/80"
                >
                  {ns.org_id}
                </Link>
                <CopyButton value={ns.org_id} label="Copy org ID" />
              </div>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Region
              </div>
              <div className="mt-1 font-mono text-sm text-gray-900">
                <Link
                  to={`/clusters/${ns.region}`}
                  className="underline underline-offset-2 hover:text-foreground/80"
                >
                  {ns.region}
                </Link>
              </div>
            </div>
            {storagePrefixes.length > 0 ? (
              <StoragePrefixes prefixes={storagePrefixes} />
            ) : null}
          </div>

          {/* Storage — 4 cols */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCell label="Documents" value={formatNumber(ns.num_vectors)} />
            <StatCell
              label="Billable Storage"
              value={
                ns.billable_bytes != null ? formatBytes(ns.billable_bytes) : "—"
              }
            />
            <StatCell
              label="Logical Bytes"
              value={
                ns.logical_bytes != null ? formatBytes(ns.logical_bytes) : "—"
              }
            />
            <StatCell
              label="Physical Bytes"
              value={
                ns.physical_bytes != null ? formatBytes(ns.physical_bytes) : "—"
              }
            />
          </div>

          {/* CMEK — 1 col, only if enabled */}
          {ns.cmek ? (
            <div className="grid grid-cols-4 gap-4">
              <StatCell label="CMEK" value="Enabled" />
            </div>
          ) : null}

          {ns.shards > 0 ? (
            <div className="grid grid-cols-4 gap-4">
              <StatCell label="Shards" value={ns.shards.toString()} />
              <StatCell
                label="Sharding Version"
                value={
                  ns.sharding_version == null ? "—" : `v${ns.sharding_version}`
                }
              />
            </div>
          ) : null}

          {/* Pinning — 3 cols, only if pinned */}
          {ns.pinned_node_type || ns.pinned_node_type_resolved ? (
            <div>
              <h3 className="mb-2 text-sm font-medium text-gray-700">
                Pinning
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <StatCell
                  label="Pinned Node Type"
                  value={
                    <span className="font-mono">
                      {ns.pinned_node_type ?? "—"}
                    </span>
                  }
                />
                <StatCell
                  label="Resolved Node Type"
                  value={
                    <span className="font-mono">
                      {ns.pinned_node_type_resolved ?? "—"}
                    </span>
                  }
                />
                <StatCell
                  label="Pinned Replicas"
                  value={ns.pinned_replicas?.toString() ?? "—"}
                />
              </div>
            </div>
          ) : null}

          {/* Timestamps — 4 cols */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCell label="Created" value={fmt(ns.created_at)} />
            <StatCell label="Updated" value={fmt(ns.updated_at)} />
            <StatCell label="Last Queried" value={fmt(ns.queried_at)} />
            <StatCell label="Stale At" value={fmt(ns.stale_at)} />
          </div>

          {/* Links */}
          <div>
            <h3 className="mb-2 text-sm font-medium text-gray-700">Links</h3>
            <div className="flex flex-wrap gap-2">
              <a
                href={getDatadogLogsUrl(ns.org_id, ns.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-sm text-purple-700 transition-colors hover:bg-purple-100"
              >
                <Icon name="Dog" size={16} />
                Logs
              </a>
              <a
                href={getDatadogTracesUrl(ns.org_id, ns.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-sm text-purple-700 transition-colors hover:bg-purple-100"
              >
                <Icon name="Dog" size={16} />
                Traces
              </a>
            </div>
          </div>

          {/* Actions */}
          <div>
            <h3 className="mb-2 text-sm font-medium text-gray-700">Actions</h3>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                title={
                  reindexDisabledReason ??
                  "Opens a confirmation dialog before forcing a reindex"
                }
                disabled={reindexDisabledReason != null}
                onClick={onForceReindex}
              >
                Force Reindex
              </Button>
              <Button
                variant="secondary"
                size="sm"
                title={
                  tidyDisabledReason ??
                  "Opens a confirmation dialog before triggering a full compaction"
                }
                disabled={tidyDisabledReason != null}
                onClick={onTriggerFullCompaction}
              >
                Trigger Full Compaction
              </Button>
              <Button
                variant="secondary"
                size="sm"
                title={
                  tidyDisabledReason ??
                  "Runs print-table-state; the table.json lands in the job's logs"
                }
                disabled={tidyDisabledReason != null}
                onClick={onPrintTableState}
              >
                Get Table Schema
              </Button>
              <Button
                variant="secondary"
                size="sm"
                title={
                  warmCacheDisabledReason ??
                  "Warms the query cache for this namespace, fanning out to every shard"
                }
                disabled={warmCacheDisabledReason != null}
                onClick={onWarmCache}
              >
                Warm Cache
              </Button>
            </div>
            {clusterError ? (
              <p className="mt-2 text-sm text-red-600">
                Couldn&apos;t load cluster details: {clusterError}
              </p>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function NamespaceDetails() {
  const [ns, setNs] = useState<Namespace | null>(null);
  const [cluster, setCluster] = useState<Cluster | null>(null);
  const [clusterError, setClusterError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  // Prefills the palette. Null when the keybinding opened it, which lands on
  // the operation list instead.
  const [preset, setPreset] = useState<{
    operation: string;
    arguments: Record<string, any>;
  } | null>(null);
  const navigate = useNavigate();
  const { orgId, namespaceId, region } = useParams();
  const { status, setIsAuthorized } = useAuth();

  useKeybindings(
    useMemo(
      () => ({
        "$mod+k": (e: KeyboardEvent) => {
          e.preventDefault();
          setPreset(null);
          setIsCommandPaletteOpen(true);
        },
      }),
      [],
    ),
    { enabled: !isCommandPaletteOpen },
  );

  useEffect(() => {
    if (!orgId || !namespaceId || !region) return;

    // Ignore a response whose route params are no longer current
    let ignore = false;
    setNs(null);
    setError(null);
    api
      .getNamespace(orgId, namespaceId, region)
      .then((data) => {
        if (ignore) return;
        setNs(data);
        setError(null);
      })
      .catch((err: any) => {
        if (ignore) return;
        if (err.status === 401) setIsAuthorized(false);
        setError(err.message ?? "Failed to fetch namespace");
      });

    return () => {
      ignore = true;
    };
  }, [orgId, namespaceId, region, setIsAuthorized]);

  useEffect(() => {
    if (!region) return;

    // Ignore a response whose region is no longer current
    let ignore = false;
    setCluster(null);
    setClusterError(null);
    api
      .getCluster(region)
      .then((data) => {
        if (ignore) return;
        setCluster(data);
        setClusterError(null);
      })
      .catch((e) => {
        if (ignore) return;
        setClusterError(e instanceof Error ? e.message : String(e));
      });

    return () => {
      ignore = true;
    };
  }, [region]);

  if (status !== "authorized") return <SessionExpired />;

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Error</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-600">{error}</p>
          <Button className="mt-4" onClick={() => navigate("/")}>
            Back to Home
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!ns) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading...</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Loading namespace details...</p>
        </CardContent>
      </Card>
    );
  }

  const disabledReason = (operation: string, label: string) =>
    clusterError
      ? `Couldn't load cluster details: ${clusterError}`
      : !cluster
        ? "Loading cluster details..."
        : !cluster.possible_operations?.[operation]
          ? `${label} isn't available on this cluster`
          : undefined;

  const openPreset = (operation: string, args: Record<string, any>) => {
    setPreset({ operation, arguments: args });
    setIsCommandPaletteOpen(true);
  };

  return (
    <>
      <NamespaceDetailsCard
        ns={ns}
        onForceReindex={() =>
          openPreset("reindex", {
            org_id: ns.org_id,
            namespace: ns.id,
            force: true,
          })
        }
        reindexDisabledReason={disabledReason("reindex", "Reindex")}
        onTriggerFullCompaction={() =>
          openPreset("tidy", {
            job: "trigger-full-compaction",
            args: [ns.org_id, ns.id],
            envs: [],
          })
        }
        onPrintTableState={() =>
          openPreset("tidy", {
            job: "print-table-state",
            args: [ns.org_id, ns.id],
            envs: [],
          })
        }
        tidyDisabledReason={disabledReason("tidy", "Tidy")}
        onWarmCache={() =>
          openPreset("warm_cache", {
            org_id: ns.org_id,
            namespace_pattern: ns.id,
            reinsert: false,
          })
        }
        warmCacheDisabledReason={disabledReason("warm_cache", "Warm cache")}
        clusterError={clusterError ?? undefined}
      />
      <ClusterOperationsCommandPalette
        isOpen={isCommandPaletteOpen}
        setIsOpen={(open) => {
          setIsCommandPaletteOpen(open);
          if (!open) setPreset(null);
        }}
        clusters={cluster ? [cluster] : []}
        initialOperation={preset?.operation}
        initialArguments={preset?.arguments}
      />
    </>
  );
}
